import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { BuildingDTO } from "shared/types/building";
import { useSession } from "@clerk/clerk-react";
import { useSearchParams } from "react-router-dom";

import {
  ProjectStatus,
  RenovationLabel,
} from "shared/types/renovation";
import { RenovationProject } from "shared/types/renovation";
import { toast } from "react-toastify";
import { DocumentUploadModal } from "./modals/documentUploadModal";
import { BuildingDocumentsByProjectSection } from "./components/documents/BuildingDocumentsByProjectSection";
import { BuildingRenovationLabels } from "./components/BuildingRenovationLabels";
import { RenovationInvoiceImportModal } from "components/renovationInvoices/renovationInvoiceImportModal";
import { ProjectsSection } from "./components/projects/ProjectsSection";
import { InvestmentItemsSection, InvestmentItemsFilters } from "./components/investmentItems";
import { BudgetBucketsSection } from "./components/budgetBuckets/BudgetBucketsSection";
import { LabelBudgetStatisticsSection } from "./components/labelStatistics/LabelBudgetStatisticsSection";
import { UnitBudgetStatisticsSection } from "./components/labelStatistics/UnitBudgetStatisticsSection";
import { FilterBar } from "shared/components/filterBar";
import type { FilterSection } from "shared/components/filterBar/types";
import { useUsers } from "shared/contexts/users.context";
import {
  INVESTMENT_STATUS_LABELS,
  InvestmentItemStatus,
} from "screens/investment/types/investment";

interface RenovationTabProps {
  building: BuildingDTO;
}

interface RenovationInvoice {
  partner_id: [number, string];
  date: string;
  name: string;
  move_name: string;
  move_line_id: [number, string];
  amount: number;
  amount_untaxed: number;
  general_account_id: [number, string];
  account_id: [number, string];
  invoice_pdf?: {
    name: string;
    data: string;
  };
}

// Active section type for context-aware FilterBar
type ActiveSection = "investment-items" | "projects";

// Cosmetic label mapping for project status
const getStatusDisplayLabel = (status: ProjectStatus): string => {
  switch (status) {
    case ProjectStatus.DESIGN:
      return "Ontwerp";
    case ProjectStatus.PLANNED:
      return "Uitvoeringsdossier";
    case ProjectStatus.IN_PROGRESS:
      return "In uitvoering - Werf";
    case ProjectStatus.COMPLETED:
      return "Voorlopige oplevering";
    case ProjectStatus.PAID:
      return "Definitieve oplevering (Volledig gefactureerd)";
    default:
      return status;
  }
};

export const RenovationTab = ({ building }: RenovationTabProps) => {
  const [renovationInvoices, setRenovationInvoices] = useState<
    RenovationInvoice[]
  >([]);
  const { session } = useSession();
  const sessionRef = useRef(session);
  const { users } = useUsers();
  const [renovationProjects, setRenovationProjects] = useState<
    RenovationProject[]
  >([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [isDocumentUploadModalOpen, setIsDocumentUploadModalOpen] =
    useState(false);
  const [isInvoiceImportModalOpen, setIsInvoiceImportModalOpen] =
    useState(false);

  // Active section for context-aware FilterBar
  const [activeSection, setActiveSection] = useState<ActiveSection>("investment-items");

  // Section refs for scroll detection
  const investmentSectionRef = useRef<HTMLDivElement>(null);
  const projectsSectionRef = useRef<HTMLDivElement>(null);

  // FilterBar states for Projects
  const [projectsSearchText, setProjectsSearchText] = useState("");
  const [phaseFilters, setPhaseFilters] = useState<string[]>([]);
  const [categoryFilters, setCategoryFilters] = useState<string[]>([]);

  // FilterBar states for Investment Items
  const [investmentSearchText, setInvestmentSearchText] = useState("");
  const [investmentStatusFilter, setInvestmentStatusFilter] = useState<string[]>([]);
  const [investmentLabelFilter, setInvestmentLabelFilter] = useState<number[]>([]);

  // Renovation labels for investment items
  const [renovationLabels, setRenovationLabels] = useState<RenovationLabel[]>([]);

  // Update session ref on each render to always have current session
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  // URL parameter handling for highlighting
  const [searchParams, setSearchParams] = useSearchParams();
  const [highlightProjectId, setHighlightProjectId] = useState<number | null>(
    null,
  );

  // Scroll position detection for context-aware FilterBar
  useEffect(() => {
    const handleScroll = () => {
      const investmentSection = investmentSectionRef.current;
      const projectsSection = projectsSectionRef.current;

      if (!investmentSection || !projectsSection) return;

      const viewportHeight = window.innerHeight;
      const investmentRect = investmentSection.getBoundingClientRect();
      const projectsRect = projectsSection.getBoundingClientRect();

      // Calculate how much of each section is visible in the viewport
      const investmentVisible = Math.max(0, Math.min(investmentRect.bottom, viewportHeight) - Math.max(investmentRect.top, 0));
      const projectsVisible = Math.max(0, Math.min(projectsRect.bottom, viewportHeight) - Math.max(projectsRect.top, 0));

      // Set active section based on which section has more visibility
      // Also consider if the top of projects section is above the middle of viewport
      const viewportMiddle = viewportHeight / 2;

      if (projectsRect.top < viewportMiddle && projectsVisible > 0) {
        setActiveSection("projects");
      } else if (investmentVisible > 0) {
        setActiveSection("investment-items");
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll(); // Initial check

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Fetch renovation labels
  useEffect(() => {
    const fetchLabels = async () => {
      if (!session) return;
      try {
        const response = await fetchWithAuth(
          `${API_URL}/renovation/labels`,
          { method: "GET" },
          session
        );
        if (response.ok) {
          const data = await response.json();
          setRenovationLabels(data || []);
        }
      } catch (error) {
        console.error("Failed to fetch renovation labels:", error);
      }
    };
    fetchLabels();
  }, [session]);

  const fetchRenovationInvoices = useCallback(async () => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/building/${building.id}/renovation-invoices`,
        { method: "GET" },
        sessionRef.current,
      );
      const data = await response.json();
      setRenovationInvoices(data);
    } catch (error) {
      console.error("Error fetching renovation invoices:", error);
    }
  }, [building.id]);

  useEffect(() => {
    fetchRenovationInvoices();
  }, [fetchRenovationInvoices]);

  const fetchProjects = useCallback(async () => {
    if (!building?.id) {
      console.log("[PERF] No building ID available");
      return;
    }

    const startTime = Date.now();
    console.log(
      `[PERF] Starting to fetch projects for building: ${building.id}`,
    );
    setLoadingProjects(true);

    try {
      const fetchStart = Date.now();
      const response = await fetchWithAuth(
        `${API_URL}/renovation/project/building/${building.id}`,
        { method: "GET" },
        sessionRef.current,
      );

      console.log(
        `[PERF] API request completed in ${Date.now() - fetchStart}ms`,
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch projects: ${response.statusText}`);
      }

      const parseStart = Date.now();
      const data = await response.json();
      console.log(
        `[PERF] JSON parsing completed in ${Date.now() - parseStart}ms`,
      );

      const projectCount = Array.isArray(data) ? data.length : 0;
      const totalParts = Array.isArray(data)
        ? data.reduce((sum, p) => sum + (p.parts?.length || 0), 0)
        : 0;

      console.log(
        `[PERF] Fetched ${projectCount} projects with ${totalParts} total parts in ${Date.now() - startTime}ms`,
      );
      setRenovationProjects(data);
    } catch (error) {
      const totalTime = Date.now() - startTime;
      console.error(
        `[PERF] Error fetching projects after ${totalTime}ms:`,
        error,
      );
      toast.error("Error loading projects");
    } finally {
      setLoadingProjects(false);
    }
  }, [building?.id]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Handle URL parameters for highlighting - but only after projects are loaded
  useEffect(() => {
    // Wait until projects are loaded and not loading
    if (loadingProjects || renovationProjects.length === 0) return;

    const highlightParam = searchParams.get("highlightProject");

    if (highlightParam) {
      const projectId = parseInt(highlightParam, 10);
      // Check if the project actually exists in the loaded projects
      const projectExists = renovationProjects.some((p) => p.id === projectId);

      if (projectExists) {
        setHighlightProjectId(projectId);
        console.log("Setting highlight for project:", projectId);

        // Clear the parameter after processing
        const newParams = new URLSearchParams(searchParams);
        newParams.delete("highlightProject");
        setSearchParams(newParams, { replace: true });
      }
    }
  }, [searchParams, setSearchParams, loadingProjects, renovationProjects]);

  // Clear highlight after a delay
  useEffect(() => {
    if (highlightProjectId) {
      const timer = setTimeout(() => {
        setHighlightProjectId(null);
      }, 3000); // Clear highlight after 3 seconds

      return () => clearTimeout(timer);
    }
  }, [highlightProjectId]);

  console.log("Current renovation projects:", renovationProjects);

  // Track which building we've loaded documents for to maintain stability across re-renders
  const loadedBuildingRef = useRef<string | null>(null);

  // Use pagination hook for documents
  // Pagination state
  const [paginationState, setPaginationState] = useState({
    documents: [],
    totalCount: 0,
    currentPage: 0,
    hasMore: false,
    isLoading: false,
    isLoadingMore: false,
    isFetchingAll: false,
    fetchAllProgress: null,
  });

  // Load documents function
  const loadDocuments = useCallback(async () => {
    if (!building.id || !session) return;

    // Mark this building as loaded
    loadedBuildingRef.current = building.id;

    setPaginationState((prev) => ({ ...prev, isLoading: true }));

    try {
      const response = await fetchWithAuth(
        `${API_URL}/renovation/building/${building.id}/documents/paginated?page=1&pageSize=30`,
        { method: "GET" },
        session,
      );

      if (!response.ok) throw new Error("Failed to fetch documents");

      const result = await response.json();

      setPaginationState({
        documents: result.data || [],
        totalCount: result.totalCount || 0,
        currentPage: 1,
        hasMore: result.hasMore || false,
        isLoading: false,
        isLoadingMore: false,
        isFetchingAll: false,
        fetchAllProgress: null,
      });
    } catch (error) {
      console.error("Error fetching documents:", error);
      toast.error("Fout bij ophalen documenten");
      setPaginationState((prev) => ({ ...prev, isLoading: false }));
    }
  }, [building.id, session]);

  // Load more documents (next page)
  const loadMore = useCallback(async () => {
    if (
      !building.id ||
      !session ||
      !paginationState.hasMore ||
      paginationState.isLoadingMore
    )
      return;

    console.log("[LOAD MORE] Starting - Current state:", {
      currentDocuments: paginationState.documents.length,
      currentPage: paginationState.currentPage,
      hasMore: paginationState.hasMore,
    });

    setPaginationState((prev) => ({ ...prev, isLoadingMore: true }));

    try {
      const nextPage = paginationState.currentPage + 1;
      const response = await fetchWithAuth(
        `${API_URL}/renovation/building/${building.id}/documents/paginated?page=${nextPage}&pageSize=30`,
        { method: "GET" },
        session,
      );

      if (!response.ok) throw new Error("Failed to fetch more documents");

      const result = await response.json();

      console.log("[LOAD MORE] Backend response:", {
        receivedDocuments: result.data?.length || 0,
        hasMore: result.hasMore,
        totalCount: result.totalCount,
        currentPage: result.currentPage,
      });

      setPaginationState((prev: any) => ({
        ...prev,
        documents: [...prev.documents, ...(result.data || [])],
        currentPage: nextPage,
        hasMore: result.hasMore || false,
        isLoadingMore: false,
      }));

      console.log("[LOAD MORE] Updated state:", {
        totalDocuments:
          paginationState.documents.length + (result.data?.length || 0),
        hasMore: result.hasMore || false,
      });
    } catch (error) {
      console.error("Error loading more documents:", error);
      toast.error("Fout bij laden van meer documenten");
      setPaginationState((prev) => ({ ...prev, isLoadingMore: false }));
    }
  }, [
    building.id,
    session,
    paginationState.hasMore,
    paginationState.currentPage,
    paginationState.isLoadingMore,
    paginationState.documents.length,
  ]);

  // Load documents only when building ID changes (not on every re-render)
  useEffect(() => {
    // Only load if we haven't loaded documents for this building yet
    // OR if the building has changed
    if (building.id && session && loadedBuildingRef.current !== building.id) {
      loadDocuments();
    }
  }, [building.id, session, loadDocuments]);

  const handleInvoiceImportSuccess = useCallback(async () => {
    try {
      await fetchRenovationInvoices();
      // Reload documents after invoice import
      await loadDocuments();
    } catch {
      console.error("Error fetching invoices");
    }
  }, [fetchRenovationInvoices, loadDocuments]);

  // Handle investment items filter changes from child component
  const handleInvestmentFilterChange = useCallback((filters: InvestmentItemsFilters) => {
    setInvestmentSearchText(filters.searchText);
    setInvestmentStatusFilter(filters.statusFilter);
    setInvestmentLabelFilter(filters.labelFilter);
  }, []);

  // FilterBar filter options for Projects
  const availableStatuses = useMemo(() => Object.values(ProjectStatus), []);
  const availableLabels: RenovationLabel[] = useMemo(
    () =>
      Array.from(
        new Map(
          renovationProjects
            .flatMap(
              (p) =>
                (p.parts || [])
                  .map((part) => part.label)
                  .filter(Boolean) as RenovationLabel[],
            )
            .map((label) => [label!.id, label!]),
        ).values(),
      ),
    [renovationProjects],
  );

  // FilterBar update function for Projects
  const updateProjectFilters = useCallback((key: string, values: string[]) => {
    switch (key) {
      case "phase":
        setPhaseFilters(values);
        break;
      case "category":
        setCategoryFilters(values);
        break;
    }
  }, []);

  // FilterBar update function for Investment Items
  const updateInvestmentFilters = useCallback((key: string, values: string[]) => {
    switch (key) {
      case "status":
        setInvestmentStatusFilter(values);
        break;
      case "label":
        setInvestmentLabelFilter(values.map(v => parseInt(v, 10)));
        break;
    }
  }, []);

  // FilterBar sections configuration for Projects
  const projectsFilterSections: FilterSection[] = useMemo(
    () => [
      {
        key: "phase",
        label: "Fase",
        options: availableStatuses.map((status) => ({
          key: status as string,
          label: getStatusDisplayLabel(status as ProjectStatus),
        })),
        selectedValues: phaseFilters,
        onUpdate: (values) => updateProjectFilters("phase", values),
        color: "moss" as const,
      },
      {
        key: "category",
        label: "Categorieën",
        options: availableLabels.map((label) => ({
          key: String(label.id),
          label: label.name,
        })),
        selectedValues: categoryFilters,
        onUpdate: (values) => updateProjectFilters("category", values),
        color: "neutral" as const,
      },
    ],
    [
      availableStatuses,
      availableLabels,
      phaseFilters,
      categoryFilters,
      updateProjectFilters,
    ],
  );

  // FilterBar sections configuration for Investment Items
  const investmentFilterSections: FilterSection[] = useMemo(
    () => [
      {
        key: "status",
        label: "Status",
        options: Object.entries(INVESTMENT_STATUS_LABELS).map(([value, label]) => ({
          key: value,
          label,
        })),
        selectedValues: investmentStatusFilter,
        onUpdate: (values) => updateInvestmentFilters("status", values),
        color: "moss" as const,
      },
      {
        key: "label",
        label: "Label",
        options: renovationLabels.map((label) => ({
          key: String(label.id),
          label: label.name,
        })),
        selectedValues: investmentLabelFilter.map(String),
        onUpdate: (values) => updateInvestmentFilters("label", values),
        color: "brick" as const,
      },
    ],
    [
      investmentStatusFilter,
      investmentLabelFilter,
      renovationLabels,
      updateInvestmentFilters,
    ],
  );

  // Get context-aware filter sections based on active section
  const currentFilterSections = activeSection === "projects"
    ? projectsFilterSections
    : investmentFilterSections;

  const currentSearchText = activeSection === "projects"
    ? projectsSearchText
    : investmentSearchText;

  const setCurrentSearchText = activeSection === "projects"
    ? setProjectsSearchText
    : setInvestmentSearchText;

  const currentSearchPlaceholder = activeSection === "projects"
    ? "Zoek projecten op titel, verantwoordelijke, onderdelen..."
    : "Zoek investeringslijnen op titel, beschrijving...";

  // FilterBar filtering logic for Projects
  const applyFilterBarFilters = useCallback(
    (projectsToFilter: RenovationProject[]) => {
      if (!projectsToFilter?.length) return [];

      return projectsToFilter.filter((project) => {
        // Phase filter
        if (phaseFilters.length > 0 && !phaseFilters.includes(project.status)) {
          return false;
        }

        // Category filter (labels on any part)
        if (categoryFilters.length > 0) {
          const categoryFilterIds = categoryFilters.map((id) =>
            parseInt(id, 10),
          );
          const partHasSelectedLabel = (project.parts || []).some(
            (part: any) =>
              part.label && categoryFilterIds.includes(part.label.id),
          );
          if (!partHasSelectedLabel) return false;
        }

        // Search filter
        const term = projectsSearchText.trim().toLowerCase();
        if (term) {
          const responsible = users.find(
            (u) => u.id === project.responsibleUserId,
          );
          const responsibleName = responsible
            ? `${responsible.first_name} ${responsible.last_name}`.toLowerCase()
            : "";
          const partTitles = (project.parts || [])
            .map((part: any) => part.title?.toLowerCase())
            .filter(Boolean)
            .join(" ");
          const partLabels = (project.parts || [])
            .map((part: any) => part.label?.name?.toLowerCase())
            .filter(Boolean)
            .join(" ");

          const matches =
            project.title?.toLowerCase().includes(term) ||
            project.description?.toLowerCase().includes(term) ||
            responsibleName.includes(term) ||
            partTitles.includes(term) ||
            partLabels.includes(term);

          if (!matches) return false;
        }

        return true;
      });
    },
    [projectsSearchText, phaseFilters, categoryFilters, users],
  );

  // Apply FilterBar filters
  const filteredProjects = useMemo(() => {
    return applyFilterBarFilters(renovationProjects);
  }, [renovationProjects, applyFilterBarFilters]);

  // FilterBar filter chips for Projects
  const projectsFilterChips = useMemo(() => {
    const chips: Array<{
      key: string;
      label: string;
      category?: string;
      color?: "moss" | "brick" | "yellow" | "neutral";
      onRemove: () => void;
    }> = [];

    // Phase filter chips
    phaseFilters.forEach((phase) => {
      chips.push({
        key: `phase-${phase}`,
        label: phase,
        category: "Fase",
        color: "moss" as const,
        onRemove: () =>
          setPhaseFilters((prev) => prev.filter((p) => p !== phase)),
      });
    });

    // Category filter chips
    categoryFilters.forEach((categoryId) => {
      const label = availableLabels.find((l) => String(l.id) === categoryId);
      if (label) {
        chips.push({
          key: `category-${categoryId}`,
          label: label.name,
          category: "Categorie",
          color: "neutral" as const,
          onRemove: () =>
            setCategoryFilters((prev) => prev.filter((c) => c !== categoryId)),
        });
      }
    });

    return chips;
  }, [phaseFilters, categoryFilters, availableLabels]);

  // FilterBar filter chips for Investment Items
  const investmentFilterChips = useMemo(() => {
    const chips: Array<{
      key: string;
      label: string;
      category?: string;
      color?: "moss" | "brick" | "yellow" | "neutral";
      onRemove: () => void;
    }> = [];

    // Status filter chips
    investmentStatusFilter.forEach((status) => {
      chips.push({
        key: `status-${status}`,
        label: INVESTMENT_STATUS_LABELS[status as InvestmentItemStatus] || status,
        category: "Status",
        color: "moss" as const,
        onRemove: () =>
          setInvestmentStatusFilter((prev) => prev.filter((s) => s !== status)),
      });
    });

    // Label filter chips
    investmentLabelFilter.forEach((labelId) => {
      const label = renovationLabels.find((l) => l.id === labelId);
      if (label) {
        chips.push({
          key: `label-${labelId}`,
          label: label.name,
          category: "Label",
          color: "brick" as const,
          onRemove: () =>
            setInvestmentLabelFilter((prev) => prev.filter((l) => l !== labelId)),
        });
      }
    });

    return chips;
  }, [investmentStatusFilter, investmentLabelFilter, renovationLabels]);

  // Get context-aware filter chips
  const currentFilterChips = activeSection === "projects"
    ? projectsFilterChips
    : investmentFilterChips;

  return (
    <>
      <div className="space-y-12">
        {/* Waar gaat de renovatie van dit gebouw over? De labels van alle
            projectonderdelen, opgesomd onder de gebouwcode. Ze stonden verspreid
            over de projectkaarten, dus moest je elk project openklikken om het te
            weten. Geen extra API-call: de projecten zijn hier al geladen. */}
        <BuildingRenovationLabels
          projects={renovationProjects}
          isLoading={loadingProjects}
        />

        {/* Portal target for the BudgetPipelineFilter pills. The filter is
            rendered from inside InvestmentItemsSection (where its state lives)
            but portalled here so users see the full pipeline overview at the
            very top of the renovation tab, above the strategische budgetten. */}
        <div id="budget-pipeline-portal" />

        {/* Strategic Budget Buckets Section - sits above investment items so
            PMs see the funding pots first; lines drawn from these pots and
            their audit trail are managed here. */}
        <BudgetBucketsSection buildingId={building.id || ""} />

        {/* Investment Items Section - Primary budget source */}
        <div ref={investmentSectionRef}>
          <InvestmentItemsSection
            buildingId={building.id || ""}
            onFilterChange={handleInvestmentFilterChange}
          />
        </div>

        {/* Projects Section */}
        <div ref={projectsSectionRef}>
          <ProjectsSection
            buildingId={building.id || ""}
            projects={filteredProjects}
            loading={loadingProjects}
            onUpdate={fetchProjects}
            highlightProjectId={highlightProjectId}
            documents={paginationState.documents}
            totalDocumentsCount={paginationState.totalCount}
            hasMoreDocuments={paginationState.hasMore}
            isLoadingMoreDocuments={paginationState.isLoadingMore}
            onLoadMoreDocuments={loadMore}
          />
        </div>

        {/* Financiële documenten van dit gebouw, per project en onderdeel.
            Stond hier vroeger niet: een opgeladen offerte of bestelbon was
            enkel terug te vinden in de algemene documentenlijst (bug 243/140). */}
        <BuildingDocumentsByProjectSection
          buildingId={building.id || ""}
          documents={paginationState.documents}
          totalCount={paginationState.totalCount}
          hasMore={paginationState.hasMore}
          isLoadingMore={paginationState.isLoadingMore}
          onLoadMore={loadMore}
        />

        {/* Statistics — moved to the bottom so PMs see the operational
            tables first and can drill into the analytical view at the end.
            Two charts: per renovation label, then per unit / kostenplaats. */}
        <LabelBudgetStatisticsSection buildingId={building.id || ""} />
        <UnitBudgetStatisticsSection buildingId={building.id || ""} />
      </div>

      {/* Context-aware FilterBar */}
      <FilterBar
        searchValue={currentSearchText}
        onSearchChange={setCurrentSearchText}
        searchPlaceholder={currentSearchPlaceholder}
        filterChips={currentFilterChips}
        filterSections={currentFilterSections}
        showWhenEmpty={true}
        showSpacer={false}
        contextLabel={activeSection === "projects" ? "Projecten" : "Investeringslijnen"}
      />

      <DocumentUploadModal
        isOpen={isDocumentUploadModalOpen}
        onClose={() => {
          setIsDocumentUploadModalOpen(false);
        }}
        onSuccess={() => {
          loadDocuments();
        }}
        building={building}
      />
      <RenovationInvoiceImportModal
        isOpen={isInvoiceImportModalOpen}
        onClose={() => setIsInvoiceImportModalOpen(false)}
        buildingId={building.id as string}
        onSuccess={handleInvoiceImportSuccess}
        availableProjects={renovationProjects}
        renovationInvoices={renovationInvoices}
      />
    </>
  );
};

export default RenovationTab;
